import { createFileRoute } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import {
  audiencesList,
  campaignCreativeAdditions,
  campaignFlightExtensions,
  campaignPlan,
  campaignPreflight,
  campaignSyncStatus,
  creativesList,
  creativeById,
  creativeRevisions,
  metaDeliveryContract,
  productionsList,
  venuesList,
} from '@/api/resources';
import { warm, warmBackground } from '@/api/loaders';
import { useAudiences, useCreatives, useProductions } from '@/api/hooks';
import { useCampaignPlan } from '@/api/manage-hooks';
import { QueryErrorNote } from '@/components/error-panel';
import { adaptCampaignForLegacyManage } from '@/lib/manage-screen-adapters';
import { requireCapability } from '@/lib/route-guards';
import { useSetViewShim } from '@/lib/use-set-view';
import { CreateFlow } from '@/screens/create';
import { pinnedCreativeSnapshots } from '@/lib/campaign-creative-pins';

export const Route = createFileRoute('/campaigns/$campaignId')({
  beforeLoad: requireCapability('campaigns_view'),
  // All four of these gate the screen behind a single `Loading campaign…`
  // early return, so any one of them arriving late holds the whole editor. The
  // params are known before the route renders, so there is no reason for the
  // component to be the thing that discovers it needs them.
  loader: ({ context, params }) => {
    // The publishing sequence in the right column reads the first two, and a
    // published campaign's creative step reads the additions journal to decide
    // what it may offer. All of them are below the fold and must not hold the
    // editor behind the `Loading campaign…` gate, so they warm in the
    // background — each renders its pending state until it lands, which is
    // honest rather than blank.
    warmBackground(
      context.queryClient,
      campaignPreflight(params.campaignId),
      campaignSyncStatus(params.campaignId),
      campaignCreativeAdditions(params.campaignId),
      // The header's Extend flight and the status line's extension words (2026-10-21).
      campaignFlightExtensions(params.campaignId),
      metaDeliveryContract(undefined),
    );
    // Venues blocks with the rest: the venue NAME is resolved onto the production
    // snapshot the legacy edit state takes on first render, and that snapshot is
    // never revisited, so a late arrival leaves "Venue TBD" stuck for the session.
    return warm(
      context.queryClient,
      campaignPlan(params.campaignId),
      productionsList({}),
      audiencesList({}),
      creativesList({}),
      venuesList({}),
    );
  },
  component: Page,
});

function Page() {
  const { campaignId } = Route.useParams();
  const planQuery = useCampaignPlan(campaignId);
  const audiencesQuery = useAudiences();
  const creativesQuery = useCreatives();
  // The list is only the picker. It may omit a pin (LIMIT 500), and its row is
  // always the latest revision, which may be different from the one serving.
  const pins = planQuery.data?.creatives ?? [];
  const creativeIds = [...new Set(pins.map((pin) => pin.creative_id))];
  const pinnedHeaders = useQueries({ queries: creativeIds.map((id) => creativeById(id)) });
  const pinnedArchives = useQueries({ queries: creativeIds.map((id) => creativeRevisions(id)) });
  const pinnedQueries = [...pinnedHeaders, ...pinnedArchives];
  const pinnedCreatives = pinnedCreativeSnapshots(
    pins, pinnedHeaders.map((query) => query.data), pinnedArchives.map((query) => query.data),
  );
  // Warm the production cache before mounting CreateFlow. Its legacy edit
  // state snapshots the selected production on first render.
  const productionsQuery = useProductions();
  const setView = useSetViewShim();

  if (
    planQuery.isPending
    || productionsQuery.isPending
    || audiencesQuery.isPending
    || creativesQuery.isPending
    || pinnedQueries.some((query) => query.isPending)
    || (pinnedCreatives.length < pins.length && pinnedQueries.some((query) => query.isFetching))
  ) {
    return <div className="muted" style={{ padding: 24 }}>Loading campaign…</div>;
  }
  if (planQuery.isError) {
    return (
      <QueryErrorNote
        error={planQuery.error}
        what="campaign plan"
        onRetry={() => { void planQuery.refetch(); }}
      />
    );
  }
  if (productionsQuery.isError) {
    return (
      <QueryErrorNote
        error={productionsQuery.error}
        what="productions"
        onRetry={() => { void productionsQuery.refetch(); }}
      />
    );
  }
  if (audiencesQuery.isError) {
    return (
      <QueryErrorNote
        error={audiencesQuery.error}
        what="audiences"
        onRetry={() => { void audiencesQuery.refetch(); }}
      />
    );
  }
  if (creativesQuery.isError) {
    return (
      <QueryErrorNote
        error={creativesQuery.error}
        what="creatives"
        onRetry={() => { void creativesQuery.refetch(); }}
      />
    );
  }
  const failedPin = pinnedQueries.find((query) => query.isError);
  if (failedPin) {
    return <QueryErrorNote error={failedPin.error} what="pinned creative revision"
      onRetry={() => { void failedPin.refetch(); }} />;
  }

  return (
    <CreateFlow
      campaign={{
        ...adaptCampaignForLegacyManage(planQuery.data.campaign),
        revision: planQuery.data.revision,
        audienceSegments: planQuery.data.audience_segments,
        creativeIds: planQuery.data.creatives.map((creative) => creative.creative_id),
      }}
      audiences={audiencesQuery.data}
      creatives={creativesQuery.data}
      pinnedCreatives={pinnedCreatives}
      plan={planQuery.data}
      setView={setView}
    />
  );
}
